import { useEffect, useRef, useState } from "react";
import SubjectHeader from "../SubjectHeader/SubjectHeader";
import { useLocation, useNavigate } from "react-router-dom";
import './TestPage.scss';
import Checkbox from '@mui/material/Checkbox';
import images from "../../assets/images";
import ConfirmationPopup from "../ConfirmationPopup/ConfirmationPopup";
import ResultsPopup from "../ResultsPopup/ResultsPopup";
import { GET_QUIZ, UPDATE_QUIZ_RESULTS } from "../../pages/Quiz/api/QuizQuery";
import { useLazyQuery, useMutation } from "@apollo/client";
import ToasterWithMessage from "../toaster";
import { useAppSelector } from "../../redux/app/hooks";
import { questions, QuizData } from "../../pages/Quiz/Quiz";
import { GET_ALL_QUESTIONS_BANK, UPDATE_QUESTIONS_BANK_RESULTS } from "../../pages/QuestionBank/api/QuestionsBankQuery";
import { FETCH_POLICY, PATH_ADDRESSES, SUBJECT_NOT_FOUND__FOR_TEACHER_ERROR, SUBJECT_NOT_FOUND_ERROR } from "../../utils/constants";

export type Results = {
    qs_id?: string;
    question_id?:string;
    given_option: string;
}

const extractData = (data: questions[], isQuestionBank: boolean) => {    
    return data.filter((item: questions) => item.chosen_option !== undefined && item.chosen_option !== null)
    .map((item: questions) => (
        isQuestionBank 
            ? { question_id: item.question_id, given_option: item.chosen_option }
            : { qs_id: item.qs_id, given_option: item.chosen_option }
    ));
};

const calculateCurrentIndex = (data: questions[]) => {
    const temp = data.filter((item: questions) => (
        item.chosen_option != undefined
    ));
    return temp.length === 0 ? 0 : temp.length - 1;
}

const TestPage = () => {
   
    const location = useLocation();
    const state = location?.state;
    const { Checkbox_checked, Checkbox_outline, Quiz_gif } = images;
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [openResultsPopup, setResultsPopup] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!state?.data) {
            navigate(PATH_ADDRESSES?.STUDENT_CLASSROOM_PATH || '/student-classroom');
        }
    }, [state, navigate]);

    const questionsList = state?.data?.questions || [];
    const [data, setData] = useState<questions[]>(questionsList);
    const [results, setResults] = useState<Results[]>(extractData(questionsList, state?.isQuestionBank));
    const [currentIndex, setCurrentIndex] = useState<number>(calculateCurrentIndex(questionsList));
    const selectedSubjectId = localStorage.getItem('selectedSId');
    const selectedClassRoomId = useAppSelector((state) => state.auth.selectedClassRoomId);
    const { error } = ToasterWithMessage();
    const [quizData, setQuizData] = useState<QuizData[]>([]);
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const isTimedQuiz = Boolean(
        state?.data?.durationSeconds !== undefined &&
        state?.data?.durationSeconds !== null &&
        Number(state?.data?.durationSeconds) > 0 &&
        !state?.isQuestionBank
    );

    const [timeLeft, setTimeLeft] = useState<number | null>(() => {
        if (!isTimedQuiz) return null;
        if (state?.data?.remainingSeconds !== undefined && state?.data?.remainingSeconds !== null) {
            return Number(state?.data?.remainingSeconds);
        }
        return Number(state?.data?.durationSeconds ?? 0);
    });

    const formatTime = (seconds: number) => {
        if (seconds < 0) seconds = 0;
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) {
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    useEffect(() => {
        if (!isTimedQuiz || timeLeft === null || timeLeft <= 0 || openResultsPopup) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(timer);
                    handleSubmit(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isTimedQuiz, timeLeft, openResultsPopup]);

    useEffect(() => {
        if (!isTimedQuiz) return;

        // Push state so browser back button fires popstate instead of exiting immediately
        window.history.pushState(null, '', window.location.href);

        const handlePopState = (e: PopStateEvent) => {
            e.preventDefault();
            window.history.pushState(null, '', window.location.href);
            setOpenConfirmationPopup(true);
        };

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };

        // Intercept desktop and mobile sidebar menu item navigation, profile links, and logout clicks during active quiz
        const handleNavigationClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;

            const isNavigatableMenuItem = Boolean(
                target.closest('.sidebarOption') || 
                target.closest('.setTwoMenuItemStyle') || 
                target.closest('.menu') || 
                target.closest('.submenuItem') || 
                target.closest('.adminSidebarSchoolCard') ||
                target.closest('.adminSidebarCalendarCard') ||
                target.closest('a[href]') ||
                target.closest('.schoolProfileContainer') ||
                target.closest('.profileContainer')
            );

            if (isNavigatableMenuItem) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                setOpenConfirmationPopup(true);
            }
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('click', handleNavigationClick, true);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleNavigationClick, true);
        };
    }, [isTimedQuiz]);

    const isAutoSaveRef = useRef<boolean>(false);
    const isSubmittingRef = useRef<boolean>(false);

    const [updateResults,{loading:updateLoading}] = useMutation(UPDATE_QUIZ_RESULTS, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: () => {
            if (!isAutoSaveRef.current) {
                getQuiz({
                    variables: {
                        input : {
                          cId: selectedClassRoomId,
                          sjId: selectedSubjectId,
                          limit: null,
                          offset: null,
                          qzId: state?.data?.quizId
                        }
                      }
                });
            }
            isAutoSaveRef.current = false;
        },
        onError: (err) => {
            error(err?.message);
            setTimeout(() => {
                navigate(state?.backNavigation?.path || PATH_ADDRESSES?.STUDENT_CLASSROOM_QUIZ_PATH, {
                    state: state?.backNavigation?.state
                });
            }, 2000);
        }
    });

    useEffect(() => {
        if (state?.isRetry) {
            setResults([]);
            setCurrentIndex(0);
        }
    }, [state?.isRetry]);    

    const [updateQuestionsBankResults,{loading:updateQuestionsBankLoading}] = useMutation(UPDATE_QUESTIONS_BANK_RESULTS, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: () => {
            getAllQuestionsBanks({
                variables: {
                    input : {
                        cId: selectedClassRoomId,
                        fId: state?.data?.fileId,
                        sjId: selectedSubjectId,
                    }
                  }
            })
        },
        onError: (err) => {
            error(err?.message);
            if (err?.message === SUBJECT_NOT_FOUND__FOR_TEACHER_ERROR || err?.message === SUBJECT_NOT_FOUND_ERROR) {
                setTimeout(() => {
                    navigate('/student-classroom/ebooks/materials/question-bank');
                }, 2000);
            }
        }
    })

    const [getQuiz,{loading:quizLoading}] = useLazyQuery(GET_QUIZ, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: (data) => {
          setQuizData(data?.getAllQuizzes?.allquizzes);
        },
        onError: (err) => {
            error(err?.message);
            setTimeout(() => {
                navigate(state?.backNavigation?.path || PATH_ADDRESSES?.STUDENT_CLASSROOM_QUIZ_PATH, {
                    state: state?.backNavigation?.state
                });
            }, 2000);
        }
    })

    const [getAllQuestionsBanks,{loading:questionsbankLoading}] = useLazyQuery(GET_ALL_QUESTIONS_BANK, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: (data) => {
          setQuizData(data?.getAllQuestionBanks?.allquestionbanks);
        },
        onError: (err) => {
          error(err?.message);
          if (err?.message === SUBJECT_NOT_FOUND__FOR_TEACHER_ERROR || err?.message === SUBJECT_NOT_FOUND_ERROR) {
            setTimeout(() => {
                navigate('/student-classroom/ebooks/materials/question-bank');
            }, 2000);
          }
        }
    })

    const debouncedSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedSaveProgress = (updatedResults: Results[]) => {
        if (state?.isQuestionBank) return;

        if (debouncedSaveTimeoutRef.current) {
            clearTimeout(debouncedSaveTimeoutRef.current);
        }
        debouncedSaveTimeoutRef.current = setTimeout(() => {
            isAutoSaveRef.current = true;
            updateResults({
                variables: {
                    cId: selectedClassRoomId,
                    qzId: state?.data?.quizId,
                    sjId: selectedSubjectId,
                    isContinue: true,
                    resultInfo: updatedResults
                }
            });
        }, 800);
    };

    useEffect(() => {
        return () => {
            if (debouncedSaveTimeoutRef.current) {
                clearTimeout(debouncedSaveTimeoutRef.current);
            }
        };
    }, []);

    const handleChange = (index: number) => {
        if (isTimedQuiz && timeLeft !== null && timeLeft <= 0) return;
        setIsChanged(true);

        const currentQsId = data?.[currentIndex]?.qs_id;
        const currentQuestionId = data?.[currentIndex]?.question_id;
        const newOption = String.fromCharCode(65 + index);

        let temp = [...results];
        const existingIndex = temp.findIndex((item) => 
            state?.isQuestionBank 
                ? item?.question_id === currentQuestionId 
                : item?.qs_id === currentQsId
        );

        if (existingIndex !== -1) {
            temp[existingIndex] = {
                ...temp[existingIndex],
                given_option: newOption
            };
        } else {
            const newResult: Results = state?.isQuestionBank 
                ? { question_id: currentQuestionId, given_option: newOption }
                : { qs_id: currentQsId, given_option: newOption };
            temp.push(newResult);
        }

        const cleanResults = temp.filter((item) => Boolean(item && item?.given_option));
        setResults(cleanResults);
        debouncedSaveProgress(cleanResults);
    };
    

    const onYesButtonClick = () => {
        if (isTimedQuiz) {
            handleSubmit(false);
        } else {
            handleSubmit(true);
        }
        setIsChanged(false);
        setOpenConfirmationPopup(false);
    }
  
    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
        setIsChanged(false);
        if (!isTimedQuiz) {
            navigate(state?.backNavigation?.path, { state: state?.backNavigation?.state });
        }
    }
    
    const handleNext = () => {
        if (currentIndex < data.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }

    const handleSubmit = async(isContinue:boolean) => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        if (debouncedSaveTimeoutRef.current) {
            clearTimeout(debouncedSaveTimeoutRef.current);
            debouncedSaveTimeoutRef.current = null;
        }
        isAutoSaveRef.current = false;
        try {
            const cleanResults = results.filter((item) => Boolean(item && item?.given_option));
            if (state?.isQuestionBank) {
                if (cleanResults.length > 0) {
                    await updateQuestionsBankResults({
                        variables: {
                            input: {
                                cId: selectedClassRoomId,
                                fId: state?.data?.fileId,
                                sjId: selectedSubjectId,
                                resultInfo: cleanResults
                            }
                        }
                    });
                }
            } else {
                await updateResults({
                    variables: {
                        cId: selectedClassRoomId,
                        qzId: state?.data?.quizId,
                        sjId: selectedSubjectId,
                        isContinue: isContinue,
                        resultInfo: cleanResults
                    }
                });
            }

            if (openConfirmationPopup && !isTimedQuiz) {
                await navigate(state?.backNavigation?.path, { state: state?.backNavigation?.state });
            } else {
                setResultsPopup(true);
            }
        } finally {
            isSubmittingRef.current = false;
        }
    }

    const handleCloseResultPopup = (action: string) => {
        setResultsPopup(false);
        if (action === "close") {
            navigate(state?.backNavigation?.path, { state: state?.backNavigation?.state })
        }
        else if (action === "view") {
            if(state?.isQuestionBank){
                navigate('/student-classroom/question-bank/view', { state: { ...state, data: quizData?.[0],isQuestionBank: true} });
            } else {
                navigate('/student-classroom/quiz/view', { state: { ...state, data: quizData?.[0]} });
            }
        }
    }

    const handleBack = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }
    
    return (
        <div className="TestPageContainer">
            <SubjectHeader headerName = {state?.title} backgroundColor={state?.backGroundColor} subCategory={state?.isQuestionBank ? state?.data?.filename : state?.data?.quizTitle} headerImage={state?.imageSrc} backNavigation={{ path: state?.backNavigation?.path, state: state?.backNavigation?.state }} tabName={state?.isQuestionBank ? 'Questions Bank' : 'Quiz'} setOpenPopup={setOpenConfirmationPopup} results={results} isChanged={isChanged} isTimedQuiz={isTimedQuiz}/>
            <div className="TestPageInnerContainer">
                <div className="QuestionContainer">
                    {isTimedQuiz && timeLeft !== null && (
                        <div className={`QuizTimerHeaderContainer ${timeLeft <= 60 ? 'timerWarningContainer' : ''}`}>
                            <div className="timerBadge">
                                <svg className={`clockIcon ${timeLeft <= 60 ? 'warningIcon' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span className="timerLabel">Time Remaining</span>
                            </div>
                            <span className={`timerValue ${timeLeft <= 60 ? 'timerWarning' : ''}`}>
                                {formatTime(timeLeft)}
                            </span>
                        </div>
                    )}
                    <div className="QuestionInnerContainer">
                        <div className="Question">
                           {`${ currentIndex + 1 }. ${ state?.isQuestionBank ? data?.[currentIndex]?.question : data?.[currentIndex]?.qs_info }`}
                        </div>
                        <div className="OptionsContainer">
                        {(() => {
                            const currentAnswer = results.find((item) => 
                                state?.isQuestionBank 
                                    ? item?.question_id === data?.[currentIndex]?.question_id 
                                    : item?.qs_id === data?.[currentIndex]?.qs_id
                            )?.given_option;

                            return Object.values(data?.[currentIndex]?.options || {}).map((val: any, index: number) => {                            
                              return (
                              <div className="Options" key={index} onClick={() => handleChange(index)}>
                                    <div className="OptionCode">{`${String.fromCharCode(65 + index)}.`}</div>
                                    <div className="OptionText">{ val } </div>
                                    <div className="CheckboxContainer">
                                        <Checkbox
                                            icon={<img src={Checkbox_outline} alt="checkBox"/>}
                                            checkedIcon={<img src={Checkbox_checked} alt="checkedImage"/>}

                                            checked={currentAnswer === String.fromCharCode(65 + index)}
                                        />
                                    </div>
                                </div>
                            )});
                        })()}
                        </div>
                        <div className="ButtonContainer">
                            {(() => {
                                const currentAnswer = results.find((item) => 
                                    state?.isQuestionBank 
                                        ? item?.question_id === data?.[currentIndex]?.question_id 
                                        : item?.qs_id === data?.[currentIndex]?.qs_id
                                )?.given_option;
                                return (
                                    <>
                                        <button className={`BackButton ${ currentIndex !== 0 ? "BackButtonActive" : "" }`} onClick={ handleBack }>Back</button>
                                        <button className={`NextButton ${ (currentAnswer && !(isTimedQuiz && timeLeft !== null && timeLeft <= 0)) ? "NextButtonActive" : "" }`} onClick={ (data.length - 1 === currentIndex) ? ()=>handleSubmit(false) : handleNext } disabled={ !currentAnswer || isSubmittingRef.current || updateLoading || quizLoading || updateQuestionsBankLoading || questionsbankLoading || (isTimedQuiz && timeLeft !== null && timeLeft <= 0)}>{(data.length - 1 === currentIndex) ? "Submit" : "Next"}</button>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationPopup
                open={openConfirmationPopup}
                page={isTimedQuiz ? 'okayCancel' : undefined}
                message={
                    state?.isQuestionBank
                        ? `Do you want to submit?`
                        : isTimedQuiz
                        ? `Navigating away will submit your quiz attempt. Do you want to submit now?`
                        : 'Do you wish to save and continue later?'
                }
                gifData={ Quiz_gif }
                yesButtonFunctionality={onYesButtonClick}
                noButtonFunctionality={onNoButtonClick}
                messageWidth={isTimedQuiz ? '280px' : '195px'}
            />
            {(quizData.length > 0) && (
                <ResultsPopup 
                    open={openResultsPopup}
                    data={quizData?.[0]}
                    isQuestionBank={state?.isQuestionBank}
                    handleClick={handleCloseResultPopup}
                />
            )}
        </div>
    )
}

export default TestPage;